import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";

export default function Reserver() {
  const [params] = useSearchParams();
  const { t, money } = useI18n();
  const [listings, setListings] = useState([]);
  const [form, setForm] = useState({
    listing_id: "", customer_name: "", email: "", phone: "",
    start_date: "", end_date: "", guests: 1, message: "", type: "reservation",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/listings").then(({ data }) => {
      setListings(data);
      const pre = params.get("listing");
      if (pre) setForm((f) => ({ ...f, listing_id: pre }));
    }).catch(() => {});
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.listing_id) { toast.error(t("reserve.errSelect")); return; }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        ...form,
        guests: Number(form.guests),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      });
      toast.success(t("reserve.success"));
      setForm({ listing_id: "", customer_name: "", email: "", phone: "", start_date: "", end_date: "", guests: 1, message: "", type: "reservation" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const selected = listings.find((l) => l.id === form.listing_id);
  const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3 text-sm text-[#F5F0E1] [color-scheme:dark] outline-none focus:border-[#D4AF37] transition-colors";

  return (
    <div className="min-h-screen pt-[72px]">
      <div className="relative py-16 sm:py-24 lg:py-28 border-b border-[#D4AF37]/10 overflow-hidden">
        <img src="/media/riviera-background.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover blur-[3px] scale-110" />
        <div className="absolute inset-0 bg-[#05060A]/72" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05060A]/55 to-[#05060A]" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12">
          <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="text-[11px] tracking-luxe uppercase text-[#E5C378] mb-4">{t("reserve.badge")}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F0E1] drop-shadow-lg">{t("reserve.title")}</motion.h1>
          <p className="text-[#D8DDD7] mt-4 max-w-2xl drop-shadow">{t("reserve.subtitle")}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-14">
        <form onSubmit={submit} className="rounded-3xl border border-[#D4AF37]/12 bg-[#0E0D0A]/60 backdrop-blur-xl p-6 sm:p-9 space-y-5" data-testid="reserve-form">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#7E8C84] block mb-2">{t("reserve.desired")}</label>
            <select required value={form.listing_id} data-testid="reserve-listing-select" onChange={(e) => setForm({ ...form, listing_id: e.target.value })} className={inputCls}>
              <option value="" className="bg-[#0E0D0A]">{t("reserve.selectPh")}</option>
              {["cars", "yachts", "properties"].map((cat) => (
                <optgroup key={cat} label={t(`categoryLabel.${cat}`)}>
                  {listings.filter((l) => l.category === cat).map((l) => (
                    <option key={l.id} value={l.id} className="bg-[#0E0D0A]">{`${l.title} — ${l.location}`}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {selected && (
            <div className="flex items-center gap-4 rounded-2xl bg-white/[0.04] border border-[#D4AF37]/20 p-3">
              <img src={selected.images?.[0]} alt="" className="w-20 h-16 object-cover rounded-2xl" />
              <div className="flex-1">
                <div className="font-display text-lg text-[#F5F0E1]">{selected.title}</div>
                <div className="text-[#D4AF37] text-sm font-mono2">{money(selected.customerPrice, selected.priceUnit)}</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button type="button" onClick={() => setForm({ ...form, type: "reservation" })} className={`rounded-2xl py-3 text-sm tracking-widest uppercase border transition-colors ${form.type === "reservation" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/10 text-[#F5F0E1]"}`} data-testid="reserve-type-reservation">{t("reserve.reservation")}</button>
            <button type="button" onClick={() => setForm({ ...form, type: "quote" })} className={`rounded-2xl py-3 text-sm tracking-widest uppercase border transition-colors sm:col-span-2 ${form.type === "quote" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/10 text-[#F5F0E1]"}`} data-testid="reserve-type-quote">{t("reserve.quote")}</button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input required placeholder={t("reserve.fullName")} value={form.customer_name} data-testid="reserve-name" onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={inputCls} />
            <input required type="email" placeholder={t("reserve.email")} value={form.email} data-testid="reserve-email" onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <input placeholder={t("reserve.phone")} value={form.phone} data-testid="reserve-phone" onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
            <input type="number" min="1" placeholder={t("reserve.guests")} value={form.guests} data-testid="reserve-guests" onChange={(e) => setForm({ ...form, guests: e.target.value })} className={inputCls} />
            {form.type === "reservation" && (
              <>
                <div><label className="text-[10px] uppercase tracking-widest text-[#7E8C84] block mb-1.5">{t("reserve.arrival")}</label><input type="date" value={form.start_date} data-testid="reserve-start" onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} /></div>
                <div><label className="text-[10px] uppercase tracking-widest text-[#7E8C84] block mb-1.5">{t("reserve.departure")}</label><input type="date" value={form.end_date} data-testid="reserve-end" onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} /></div>
              </>
            )}
          </div>

          <textarea placeholder={t("reserve.messagePh")} rows={4} value={form.message} data-testid="reserve-message" onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} />

          <button type="submit" disabled={submitting} data-testid="reserve-submit" className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[#D4AF37] hover:bg-[#B8912A] text-black font-semibold py-4 text-sm tracking-widest uppercase transition-colors disabled:opacity-50">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {t("reserve.send")}
          </button>
        </form>
      </div>
    </div>
  );
}
